/**
 * Merch & Gifts slider — scroll-snap rail with dots + next-arrow + count text.
 *
 * Pages, not cards: cardsPerPage is derived from
 * `viewport.clientWidth / cardWidth` so the section auto-adapts at any
 * breakpoint (1+peek mobile, 3 tablet, --merch-per-view desktop).
 *
 * Differences vs reviews-slider.js:
 *   - Optional [data-merch-prev] / [data-merch-next] arrow buttons get
 *     wired up with a disabled-at-edge state.
 *   - Optional [data-merch-count] element re-renders its template
 *     (placeholders {{ visible }} / {{ total }}) on every page change.
 *
 * Re-runs on shopify:section:load so the editor preview rebinds.
 */
(function () {
  if (typeof document === 'undefined') return;

  var initOne = function (slider) {
    if (slider.dataset.merchSliderBound === '1') return;
    slider.dataset.merchSliderBound = '1';

    var viewport = slider.querySelector('[data-merch-viewport]');
    var cards = Array.from(slider.querySelectorAll('[data-merch-card]'));
    var dotsList = slider.querySelector('[data-merch-dots]');
    var prevBtn = slider.querySelector('[data-merch-prev]');
    var nextBtn = slider.querySelector('[data-merch-next]');
    var countEl = slider.querySelector('[data-merch-count]');
    var countTemplate = countEl ? countEl.dataset.template : '';

    if (!viewport || !cards.length) return;

    var dots = [];
    var cardsPerPage = 1;
    var pageCount = 1;
    var currentPage = 0;

    var measureCardsPerPage = function () {
      var first = cards[0];
      if (!first) return 1;
      var cardWidth = first.getBoundingClientRect().width;
      if (!cardWidth) return 1;
      var perPage = Math.round(viewport.clientWidth / cardWidth);
      return Math.max(1, perPage);
    };

    var updateCount = function () {
      if (!countEl || !countTemplate) return;
      // Always reflect what's actually on screen, not what's "new" on this page.
      // The scroll-clamp on the last page keeps cardsPerPage cards visible
      // (e.g. 15 / 4 → page 4 still shows 4 cards: 12, 13, 14, 15), so a
      // simple "new products on this page" count would underreport (3 vs 4).
      var visible = Math.min(cardsPerPage, cards.length);
      countEl.textContent = countTemplate
        .replace('%visible%', visible)
        .replace('%total%', cards.length);
    };

    var setActive = function (pageIdx) {
      currentPage = pageIdx;
      dots.forEach(function (dot, j) {
        if (j === pageIdx) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      // Hide prev at page 0 and next at the last page (per design — arrows
      // appear only when navigation in that direction is meaningful).
      if (prevBtn) prevBtn.hidden = pageIdx === 0;
      if (nextBtn) nextBtn.hidden = pageIdx >= pageCount - 1;
      updateCount();
    };

    var goToPage = function (pageIdx) {
      if (pageIdx < 0) pageIdx = 0;
      if (pageIdx > pageCount - 1) pageIdx = pageCount - 1;
      var targetCardIdx = pageIdx * cardsPerPage;
      if (targetCardIdx > cards.length - 1) targetCardIdx = cards.length - 1;
      var targetCard = cards[targetCardIdx];
      if (!targetCard) return;
      var left = targetCard.offsetLeft - viewport.offsetLeft;
      var maxScroll = viewport.scrollWidth - viewport.clientWidth;
      if (left > maxScroll) left = maxScroll;
      if (left < 0) left = 0;
      viewport.scrollTo({ left: left, behavior: 'smooth' });
      setActive(pageIdx);
    };

    var buildDots = function () {
      cardsPerPage = measureCardsPerPage();
      pageCount = Math.ceil(cards.length / cardsPerPage);

      if (dotsList) {
        dotsList.innerHTML = '';
        dots = [];
        if (pageCount <= 1) {
          dotsList.hidden = true;
        } else {
          dotsList.hidden = false;
          for (var i = 0; i < pageCount; i++) {
            var li = document.createElement('li');
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'merch-gifts__dot';
            btn.dataset.pageIndex = String(i);
            btn.setAttribute('aria-label', 'Go to product page ' + (i + 1));
            if (i === 0) btn.setAttribute('aria-current', 'true');
            btn.addEventListener('click', (function (idx) {
              return function () { goToPage(idx); };
            })(i));
            li.appendChild(btn);
            dotsList.appendChild(li);
            dots.push(btn);
          }
        }
      }

      syncFromScroll();
    };

    var syncFromScroll = function () {
      if (!cards.length) return;
      var maxScroll = viewport.scrollWidth - viewport.clientWidth;
      var pageIdx;
      if (maxScroll <= 0 || pageCount <= 1) {
        pageIdx = 0;
      } else {
        var ratio = viewport.scrollLeft / maxScroll;
        pageIdx = Math.round(ratio * (pageCount - 1));
      }
      if (pageIdx < 0) pageIdx = 0;
      if (pageIdx > pageCount - 1) pageIdx = pageCount - 1;
      setActive(pageIdx);
    };

    var raf = null;
    viewport.addEventListener('scroll', function () {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(syncFromScroll);
    }, { passive: true });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(buildDots, 150);
    });

    if (prevBtn) prevBtn.addEventListener('click', function () { goToPage(currentPage - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goToPage(currentPage + 1); });

    buildDots();
  };

  var initAll = function () {
    document.querySelectorAll('[data-merch-slider]').forEach(initOne);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
